<script setup lang="ts">
import { Order } from '@/@types/order'
import { Button } from '@/components/ui/button'
import { DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { currentPriceFormat } from '@/helpers/current-price'
import { useForm } from '@inertiajs/vue3'
import { Loader2, Ticket } from 'lucide-vue-next'

const { order } = defineProps<{ order: Order }>()

const form = useForm({
    status: 'canceled',
    cancel_reason: order.cancel_reason,
    cancel_justification: order.cancel_justification,
})
</script>

<template>
    <DialogContent class="font-inter p-8 text-gray-800">
        <DialogHeader class="flex gap-2">
            <DialogTitle class="text-2xl">{{ $t('You are canceling order:', { order_id: order.id }) }}</DialogTitle>
            <DialogDescription class="text-sm font-normal text-gray-800">{{
                $t(
                    'Please provide the details below to proceed with the cancellation. By proceeding with the cancellation, all tickets will be cancelled.',
                )
            }}</DialogDescription>
        </DialogHeader>
        <div class="flex flex-col gap-3 rounded-xl bg-gray-50 p-4">
            <p class="text-base text-slate-800">
                {{ $t('Order') }}: <strong> #{{ order.id }} </strong>
            </p>
            <div v-for="orderItem in order.items" :key="orderItem.id" class="flex items-center justify-between">
                <div class="flex items-center gap-2 text-base font-semibold">
                    <span class="flex size-11 items-center justify-center rounded-full bg-cyan-50 text-cyan-700"> <Ticket /> </span>
                    {{ $t(orderItem.ticket.category) }}
                </div>
                <div class="flex gap-3 text-sm">
                    {{ orderItem.effective_quantity }}x
                    <p class="text-sm font-medium text-slate-900">
                        {{ currentPriceFormat(orderItem.ticket) }}
                    </p>
                </div>
            </div>
        </div>
        <DialogFooter>
            <form
                @submit.prevent="form.patch(`/admin/orders/${order.id}/update_status`, { preserveState: false })"
                class="flex w-full flex-col gap-6"
            >
                <div class="flex flex-col gap-1.5">
                    <Label required for="cancel_justification">{{ $t('Reason') }}</Label>
                    <Select v-model="form.cancel_reason">
                        <SelectTrigger class="w-full">
                            <SelectValue :placeholder="$t('Select')" />
                        </SelectTrigger>
                        <SelectContent class="z-[200]">
                            <SelectItem v-for="cancel_reason in order.cancel_reasons_keys" :key="cancel_reason" :value="cancel_reason">
                                {{ $t(`cancel_reason.${cancel_reason}`) }}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <p v-if="form.errors.cancel_reason" class="text-destructive text-xs">
                        {{ form.errors.cancel_reason }}
                    </p>
                </div>
                <div class="flex flex-col gap-1.5">
                    <Label for="cancel_justification">{{ $t('Justification') }}</Label>
                    <Textarea
                        id="cancel_justification"
                        v-model="form.cancel_justification"
                        :placeholder="$t('Fill in a justification for cancellation')"
                    />
                    <p v-if="form.errors.cancel_justification" class="text-destructive text-xs">
                        {{ form.errors.cancel_justification }}
                    </p>
                </div>
                <div class="flex justify-between pt-6">
                    <DialogClose>
                        <Button type="button" variant="ghost" class="text-primary hover:text-primary hover:bg-primary/3 min-w-2/12">
                            {{ $t('Back') }}
                        </Button>
                    </DialogClose>
                    <Button :disabled="form.processing || !form.cancel_reason" class="min-w-2/12">
                        <template v-if="!form.processing">
                            {{ $t('Cancel order') }}
                        </template>
                        <template v-if="form.processing">
                            <span>
                                <Loader2 class="animate-spin" />
                                <span class="sr-only"></span>
                            </span>
                        </template>
                    </Button>
                </div>
            </form>
        </DialogFooter>
    </DialogContent>
</template>
